import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import {
  CustomOAuthAppInfoResponse,
  CustomOAuthAppVisibility,
  DataSourceType,
  IdentityProvider,
  SSOAuthorizationURLResponseMode,
  UpsertCustomOAuthAppInfoRequest,
} from "@coeff/api";
import { DataSourceTypeDisplayNames, getIdProviderDisplayName } from "@coeff/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "antd/lib/form/Form";
import * as React from "react";
import { useSelector } from "react-redux";
import { Link, useHistory, useLocation, useParams, useRouteMatch } from "react-router-dom";
import styled from "styled-components";

import { QueryCacheKey } from "../api";
import {
  Alert,
  AlertExclamation,
  BorderedSelect,
  Button,
  Form,
  FormItem,
  CaretRightBlack,
  Input,
  List,
  LoaderWithPerfTimings,
  message,
  Modal,
  OAuthActions,
  OAuthCancelButton,
  OAuthConfiguredAlert,
  OAuthDeleteButton,
  OAuthEditButton,
  OAuthSaveButton,
  SettingsLayout,
} from "../components";
import { COLORS } from "../constants";
import { AppState } from "../store";
import { useApiContext, useTrack } from "../utils";

/**
 * Route slug for a custom-OAuth connector: `netflix_quiver` <-> `netflix-quiver-oauth`.
 *
 * Matches the per-connector settings pages (`snowflake-oauth`, `tableau-oauth`, ...) so
 * every OAuth settings URL reads the same way, rather than exposing the raw enum value
 * under a `custom-oauth/` prefix that means nothing to the reader.
 */
export const customOAuthRouteSlug = (dataSourceType: string): string =>
  `${dataSourceType.replace(/_/g, "-")}-oauth`;

export const dataSourceTypeFromRouteSlug = (slug: string): string => slug.replace(/-/g, "_");

type ConnectorCopy = {
  appNamePlaceholder: string;
  baseUrlPlaceholder: string;
  clientIdPlaceholder: string;
  issuerUrlPlaceholder: string;
  scopesPlaceholder: string;
  responseModePlaceholder: string;
};

const DEFAULT_COPY: ConnectorCopy = {
  appNamePlaceholder: "Production",
  baseUrlPlaceholder: "https://api.example.com",
  clientIdPlaceholder: "Client ID",
  issuerUrlPlaceholder: "https://idp.example.com/",
  scopesPlaceholder: "openid email offline_access",
  responseModePlaceholder: "Default",
};

/**
 * Per-connector placeholders. A bespoke connector is bespoke down to its examples: showing
 * a real Quiver host and client id beats example.com, since there is exactly one customer
 * and these are the values they will type. Connectors with no entry fall back to DEFAULT_COPY.
 */
const CONNECTOR_COPY: Partial<Record<DataSourceType, Partial<ConnectorCopy>>> = {
  [DataSourceType.NetflixQuiver]: {
    appNamePlaceholder: "Netflix Metrics (Test)",
    baseUrlPlaceholder: "https://quiver-coefficient.test.netflix.net/api/datajunction/semantic",
    clientIdPlaceholder: "projectquiver-coefficient-test",
    scopesPlaceholder: "default e2eToken",
    // Matches the connector's _DEFAULT_RESPONSE_MODE.
    responseModePlaceholder: "Default (form_post)",
  },
};

const { Item } = List;

const StyledAlert = styled(Alert)`
  line-height: 1.8em;
  padding: 12px;

  .ant-alert-icon {
    margin: 6px;
  }
`;

const AppRow = styled.div`
  border-radius: 6px;
`;

const StyledItem = styled(Item)`
  && {
    background: ${COLORS.black2};
    border: none;
    cursor: pointer;
    margin-bottom: 8px;
    padding: 12px;

    &:hover {
      background-color: ${COLORS.black4};
    }

    .ant-list-item-meta {
      align-items: center;
    }

    .ant-list-item-meta-title {
      color: ${COLORS.black45};
      line-height: 1.25;
    }

    .ant-list-item-meta-description {
      color: ${COLORS.black85};
      font-size: 14px;
      font-weight: bold;
    }
  }
`;

/** Flags an admins-only app in the list, so test and prod are told apart at a glance. */
const AdminOnlyTag = styled.span`
  background: ${COLORS.black6};
  border-radius: 4px;
  color: ${COLORS.black45};
  font-size: 11px;
  font-weight: normal;
  margin-left: 8px;
  padding: 1px 6px;
  text-transform: uppercase;
  vertical-align: middle;
`;

const ManageAction = styled.div`
  align-items: center;
  display: flex;
  margin: 8px 16px;
`;

const ManageText = styled.div`
  color: ${COLORS.black45};
  margin-right: 12px;
`;

const RowLink = styled(Link)`
  color: inherit;
  display: block;

  &:hover {
    color: inherit;
  }
`;

const TooltipBody = styled.div`
  p {
    margin: 0;
  }

  p + p,
  p + ul,
  ul + table {
    margin-top: 10px;
  }

  ul {
    margin: 0;
    padding-left: 18px;
  }

  code {
    display: block;
    font-family: Menlo, Monaco, Consolas, "Courier New", monospace;
    font-size: 12px;
    white-space: nowrap;
  }

  li code {
    display: inline;
  }

  th .anticon {
    margin-right: 4px;
  }

  table {
    border-collapse: collapse;
    width: 100%;
  }

  th {
    font-weight: 600;
    text-align: left;
    white-space: nowrap;
  }

  th,
  td {
    border-top: 1px solid rgba(255, 255, 255, 0.2);
    padding: 4px 8px 4px 0;
    vertical-align: top;
  }

  thead th {
    border-top: none;
  }
`;

/**
 * Worked examples for the "Default Visible Views" tooltip. Chosen to show each rule
 * once: `**` spanning zero and many segments, `*` staying inside one segment, and `*`
 * used as part of a segment.
 */
const VIEW_PATTERN_EXAMPLES: { pattern: string; matches: string[]; misses: string[] }[] = [
  {
    pattern: "growth.**",
    matches: ["growth", "growth.cubes.funnel"],
    misses: ["ads.growth"],
  },
  {
    pattern: "ads.*.spend",
    matches: ["ads.cubes.spend"],
    misses: ["ads.spend", "ads.cubes.v2.spend"],
  },
  {
    pattern: "**.spend",
    matches: ["spend", "ads.cubes.spend"],
    misses: ["ads.spend_daily"],
  },
  {
    pattern: "ads.**.*_daily",
    matches: ["ads.spend_daily", "ads.cubes.v2.spend_daily"],
    misses: ["ads.cubes.spend"],
  },
];

const Section = styled.div`
  background: ${COLORS.black2};
  border-radius: 6px;
  margin-bottom: 24px;
  padding: 16px;

  /* The last field's margin would otherwise show as dead space inside the box. */
  .ant-form-item:last-child {
    margin-bottom: 0;
  }
`;

/**
 * The Tableau and Snowflake manage pages label their fields with a bare <label> inside a
 * `FormGroup`, which lays out on an ordinary inline line box. This page is built on antd's
 * FormItem, whose label is an inline-flex block with its own height and an 8px pad beneath,
 * so left alone the two forms run at different rhythms: 74px per field here against 66px
 * there, and a heavier gap between each label and its input.
 *
 * These values were measured against those two forms rather than guessed. Together they
 * bring the label box (16px), the label-to-input gap (3px), the field pitch (66px) and the
 * 2px label inset to the same numbers.
 */
const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 12px;
  }

  .ant-form-item-label {
    padding: 0;
  }

  .ant-form-item-label > label {
    font-weight: bold;
    height: auto;
    line-height: normal;
    margin-left: 2px;
  }
`;

const SectionHeading = styled.div`
  font-weight: 600;
  margin-bottom: 16px;
`;

type FormValues = {
  app_name?: string;
  base_url?: string;
  client_id: string;
  client_secret?: string;
  issuer_url?: string;
  authorization_url?: string;
  token_url?: string;
  scopes?: string;
  id_provider?: IdentityProvider;
  response_mode_override?: SSOAuthorizationURLResponseMode;
  extra_config?: string;
  visibility?: CustomOAuthAppVisibility;
  /** One dot-glob pattern per line; the API carries them as a list. Netflix Quiver only. */
  view_glob_patterns?: string;
};

/** The non-blank, trimmed lines of the patterns textarea. */
const patternLines = (text?: string): string[] =>
  (text ?? "")
    .split("\n")
    .map(line => line.trim())
    .filter(line => line !== "");

/**
 * Values prefilled into the add form, per connector.
 *
 * Unlike a placeholder, these are submitted as-is unless the admin edits them: a bespoke
 * connector has exactly one identity provider, so making them retype its endpoints is
 * busywork with a typo attached. Editing an existing app fills from the saved app instead.
 */
const CONNECTOR_DEFAULTS: Partial<Record<DataSourceType, Partial<FormValues>>> = {
  [DataSourceType.NetflixQuiver]: {
    issuer_url: "https://meechum.prod.netflix.net/",
    authorization_url: "https://meechum.prod.netflix.net/as/authorization.oauth2",
    token_url: "https://meechum.prod.netflix.net/as/token.oauth2",
    // The namespace Netflix will want first. `**`, not `*`: every view under `shared`
    // is three segments deep, so `shared.*` matches nothing.
    view_glob_patterns: "shared.**",
    visibility: CustomOAuthAppVisibility.AllUsers,
  },
};

/**
 * Form state for a saved app.
 *
 * Shared by the initial prefill and by Cancel, which has to restore exactly what the
 * prefill put there -- two copies of this mapping would drift the moment a field is added.
 * The secret is never returned by the API, so it always comes back blank, which the save
 * path reads as "keep the current one".
 */
const formValuesFromApp = (app: CustomOAuthAppInfoResponse): FormValues => ({
  app_name: app.app_name ?? undefined,
  base_url: app.base_url ?? undefined,
  client_id: app.client_id,
  client_secret: "",
  issuer_url: app.issuer_url ?? undefined,
  authorization_url: app.authorization_url ?? undefined,
  token_url: app.token_url ?? undefined,
  scopes: app.scopes ?? undefined,
  id_provider: app.id_provider ?? undefined,
  response_mode_override: app.response_mode_override ?? undefined,
  extra_config: app.extra_config ?? undefined,
  // Apps saved before visibility existed come back unset, which the API reads as
  // "all users"; show that rather than an empty select.
  visibility: app.visibility ?? CustomOAuthAppVisibility.AllUsers,
  view_glob_patterns: app.netflix_quiver_view_glob_patterns?.join("\n") ?? undefined,
});

/**
 * Add or manage one per-domain OAuth app for a bespoke, single-customer connector.
 *
 * Generic over `data_source_type`: the connector is read from the route, so adding a new
 * custom-OAuth connector needs only an entry in `spec.CUSTOM_OAUTH_DATA_SOURCE_TYPES`,
 * not another copy of this page. That is the whole point of the shared shape -- the
 * per-connector pages (Looker, NetSuite, Snowflake, Tableau, Arrow Flight SQL) are each
 * several hundred near-identical lines.
 */
export const CustomOAuthSettings = () => {
  const { dataSourceSlug, clientId } = useParams<{
    dataSourceSlug: string;
    clientId?: string;
  }>();
  const dataSourceType = dataSourceTypeFromRouteSlug(dataSourceSlug);
  const history = useHistory();
  const { apiClient: api } = useApiContext();
  const queryClient = useQueryClient();
  const track = useTrack();
  const [form] = useForm<FormValues>();
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const [isEditMode, setIsEditMode] = React.useState(false);

  React.useEffect(() => {
    track("workspace_custom_oauth_settings_viewed", { conn_data_source: dataSourceType });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSourceType]);

  const { search } = useLocation();
  const { url } = useRouteMatch();
  const decodedClientId = clientId ? decodeURIComponent(clientId) : undefined;
  const isExistingApp = Boolean(decodedClientId);
  const isAddingNewAccount = new URLSearchParams(search).get("addNewAccount") === "true";
  // A saved app opens as a read-only summary and only becomes editable via Edit, matching
  // the per-connector pages. Adding one goes straight to an editable form.
  const isReadOnly = isExistingApp && !isEditMode;
  const typedDataSourceType = dataSourceType as DataSourceType;
  const displayName = DataSourceTypeDisplayNames[typedDataSourceType] ?? dataSourceType;
  const copy: ConnectorCopy = { ...DEFAULT_COPY, ...CONNECTOR_COPY[typedDataSourceType] };
  const isNetflixQuiver = typedDataSourceType === DataSourceType.NetflixQuiver;

  const clientFlags = useSelector((state: AppState) => state.app.clientFlags);

  const { data, isLoading } = useQuery(
    [QueryCacheKey.CUSTOM_OAUTH_APP_INFO],
    async () => (await api.listCustomOAuthAppInfo()).data
  );

  const appsForConnector: CustomOAuthAppInfoResponse[] = React.useMemo(
    () => (data?.apps_list ?? []).filter(app => app.data_source_type === typedDataSourceType),
    [data, typedDataSourceType]
  );

  const existing: CustomOAuthAppInfoResponse | undefined = React.useMemo(
    () =>
      data?.apps_list?.find(
        app => app.data_source_type === typedDataSourceType && app.client_id === decodedClientId
      ),
    [data, typedDataSourceType, decodedClientId]
  );

  // Prefill from the saved app so an admin changing one field does not have to retype
  // the rest.
  React.useEffect(() => {
    // setFieldsValue merges into the form store, and the store outlives the list view (the
    // same component instance serves every custom-OAuth route, see below), so clear it first.
    // Otherwise an Add opened after viewing an app inherits that app's values, client id
    // included, and saving would overwrite the app instead of creating one.
    form.resetFields();
    if (existing) {
      form.setFieldsValue(formValuesFromApp(existing));
    } else if (!isExistingApp) {
      form.setFieldsValue(CONNECTOR_DEFAULTS[typedDataSourceType] ?? {});
    }
  }, [existing, isExistingApp, typedDataSourceType, form]);

  // Both custom-OAuth routes render this same component, so React Router reconciles rather
  // than remounts when moving between the list and an app. Without this, an Edit opened on
  // one app would still be open on the next one.
  React.useEffect(() => {
    setIsEditMode(false);
  }, [decodedClientId]);

  const upsert = useMutation(
    (body: UpsertCustomOAuthAppInfoRequest) =>
      api.upsertCustomOAuthAppInfo({ upsert_custom_o_auth_app_info_request: body }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryCacheKey.CUSTOM_OAUTH_APP_INFO]);
        message.success(`${displayName} OAuth settings saved`);
        // Editing drops back to this app's read-only view, matching the Tableau page.
        // Creating has nothing left to show here, so it returns to the list.
        if (isExistingApp) {
          setIsEditMode(false);
        } else {
          history.push("/dashboard/settings/oauth-settings");
        }
      },
      onError: (error: any) => {
        // The upsert validates the issuer URL by actually fetching its OpenID
        // configuration, so this is where a typo'd issuer surfaces. Show the server's
        // message rather than a generic failure -- it names the problem.
        message.error(
          error?.response?.data?.reason ??
            error?.response?.data?.message ??
            "Could not save these settings. Please check the values and try again."
        );
      },
    }
  );

  const remove = useMutation(
    () =>
      api.deleteCustomOAuthAppInfo({
        delete_custom_o_auth_app_info_request: {
          data_source_type: typedDataSourceType,
          client_id: decodedClientId ?? "",
          domain: existing?.domain,
        },
      }),
    {
      onSuccess: () => {
        track("workspace_custom_oauth_app_deleted", {
          conn_data_source: typedDataSourceType,
        });
        queryClient.invalidateQueries([QueryCacheKey.CUSTOM_OAUTH_APP_INFO]);
        message.success(`${displayName} OAuth app deleted`);
        history.push("/dashboard/settings/oauth-settings");
      },
      onError: () => message.error("Could not remove this app. Please try again."),
    }
  );

  // The server rejects an app whose endpoints cannot be resolved either way
  // (custom_oauth_controller.CustomOAuthUpsertApp.post). Mirror that here so the admin
  // sees it inline on the offending field instead of as a toast after a round trip.
  const endpointsResolvableRule = {
    validator: () => {
      const { issuer_url, authorization_url, token_url } = form.getFieldsValue([
        "issuer_url",
        "authorization_url",
        "token_url",
      ]);
      if (!issuer_url && !(authorization_url && token_url)) {
        return Promise.reject(
          new Error("Provide either an Issuer URL, or both an Authorization URL and a Token URL.")
        );
      }
      return Promise.resolve();
    },
  };

  // Half a pair is never usable, so ask for the other half rather than falling through to
  // the vaguer message above.
  const pairedWithRule = (other: "authorization_url" | "token_url", label: string) => ({
    validator: (_rule: unknown, value?: string) => {
      if (!value && form.getFieldValue(other)) {
        return Promise.reject(
          new Error(`${label} is required when the other endpoint URL is set.`)
        );
      }
      return Promise.resolve();
    },
  });

  // The connector silently substitutes {} for anything that is not a JSON object
  // (netflix_quiver.QuiverClient.runtime_configuration), so a typo here would otherwise
  // only show up as a missing setting at query time, with nothing said to the admin.
  const jsonObjectRule = {
    validator: (_rule: unknown, value?: string) => {
      if (!value || !value.trim()) {
        return Promise.resolve();
      }
      let parsed: unknown;
      try {
        parsed = JSON.parse(value);
      } catch {
        return Promise.reject(new Error("This must be valid JSON."));
      }
      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        return Promise.reject(new Error('This must be a JSON object, for example {"key": 1}.'));
      }
      return Promise.resolve();
    },
  };

  // Mirrors dot_glob_utils: the one malformed pattern is `**` glued to other characters,
  // which reads as neither wildcard. Blank lines are skipped, not rejected, so a pasted
  // list with a trailing newline is fine.
  const viewPatternsRule = {
    validator: (_rule: unknown, value?: string) => {
      const bad = patternLines(value).find(line =>
        line.split(".").some(segment => segment !== "**" && segment.includes("**"))
      );
      if (bad) {
        return Promise.reject(
          new Error(`"${bad}": ** must stand alone as a whole segment, e.g. growth.**`)
        );
      }
      return Promise.resolve();
    },
  };

  // Mirrors the server's (domain, data_source_type, client_id) key, so a reused Client
  // ID is caught under the field that caused it rather than as a toast that scrolls away
  // -- the save it blocks is destructive (the colliding app's secret goes with it).
  //
  // Scoped to the caller's own domain, which is where an Add always writes: the same
  // Client ID on a sibling domain in the family is a different app, and the server would
  // rightly accept it. The upsert enforces this too (fail_if_exists), for callers that
  // are not this form.
  const ownDomain = clientFlags?.email?.split("@")[1];
  const duplicateClientIdRule = {
    validator: (_rule: unknown, value?: string) => {
      if (isExistingApp || !value?.trim()) {
        return Promise.resolve();
      }
      const clash = appsForConnector.find(
        app => app.client_id === value.trim() && app.domain === ownDomain
      );
      if (clash) {
        return Promise.reject(
          new Error(`Already used by "${clash.app_name || clash.base_url || clash.client_id}".`)
        );
      }
      return Promise.resolve();
    },
  };

  // Same shape as the Client ID rule, for a different reason: the addon's endpoint
  // picker titles each option with the name, so two apps sharing one leave the user
  // choosing between identical rows. Excludes the app being edited so re-saving it
  // without touching the name is not a collision with itself.
  const duplicateAppNameRule = {
    validator: (_rule: unknown, value?: string) => {
      const wanted = value?.trim().toLocaleLowerCase();
      if (!wanted) {
        return Promise.resolve();
      }
      const clash = appsForConnector.find(
        app =>
          app.domain === ownDomain &&
          app.client_id !== decodedClientId &&
          (app.app_name ?? "").trim().toLocaleLowerCase() === wanted
      );
      if (clash) {
        return Promise.reject(new Error("Another endpoint already has this name."));
      }
      return Promise.resolve();
    },
  };

  const handleCancelEdit = () => {
    // resetFields clears validation state as well as values, so a rejected URL does not
    // come back still flagged red the next time Edit is opened.
    form.resetFields();
    if (existing) {
      form.setFieldsValue(formValuesFromApp(existing));
    }
    setIsEditMode(false);
  };

  const handleFinish = (values: FormValues) => {
    track("workspace_custom_oauth_app_saved", { conn_data_source: typedDataSourceType });
    upsert.mutate({
      data_source_type: typedDataSourceType,
      app_name: values.app_name,
      base_url: values.base_url,
      client_id: values.client_id,
      client_secret: values.client_secret || undefined,
      issuer_url: values.issuer_url || undefined,
      authorization_url: values.authorization_url || undefined,
      token_url: values.token_url || undefined,
      scopes: values.scopes || undefined,
      id_provider: values.id_provider,
      response_mode_override: values.response_mode_override,
      extra_config: values.extra_config || undefined,
      visibility: values.visibility ?? CustomOAuthAppVisibility.AllUsers,
      // Adding, not editing: the endpoint is an upsert keyed on client id, so without
      // this a reused client id would quietly replace the app it collides with.
      fail_if_exists: !isExistingApp,
      netflix_quiver_view_glob_patterns: isNetflixQuiver
        ? patternLines(values.view_glob_patterns)
        : undefined,
      domain: existing?.domain,
    });
  };

  // Same gate as the per-connector pages. The upsert and delete endpoints enforce this
  // too (custom_oauth_controller._assert_may_administer_domain); the list endpoint does
  // not, so without this a non-admin could read the saved apps here.
  if (
    clientFlags?.admin_emails &&
    clientFlags?.admin_emails.length > 0 &&
    !clientFlags?.admin_emails?.includes(clientFlags.email)
  ) {
    return (
      <SettingsLayout
        title={`${displayName} OAuth Settings`}
        dataSourceType={typedDataSourceType}
        width="narrow"
      >
        <StyledAlert
          type="error"
          showIcon
          icon={<AlertExclamation type="error" />}
          message="You need to be an admin to access this page"
          description={`Please contact your domain admin for configuring or managing ${displayName} accounts.`}
        />
      </SettingsLayout>
    );
  }

  if (isLoading) {
    return (
      <SettingsLayout
        title={`${displayName} OAuth Settings`}
        dataSourceType={typedDataSourceType}
        width="narrow"
      >
        <LoaderWithPerfTimings name="CustomOAuthSettings" />
      </SettingsLayout>
    );
  }

  // Three modes, matching the per-connector pages: list, add, manage. A customer can run
  // several environments of the same connector (Quiver test and prod, say), so landing on
  // this route has to show which ones exist rather than assuming a first-time setup.
  if (!isExistingApp && !isAddingNewAccount && appsForConnector.length > 0) {
    return (
      <SettingsLayout
        title={`${displayName} OAuth Settings`}
        dataSourceType={typedDataSourceType}
        width="narrow"
      >
        {appsForConnector.map(appInfo => (
          <AppRow key={appInfo.client_id}>
            <RowLink to={`${url}/${encodeURIComponent(appInfo.client_id)}`}>
              <StyledItem>
                <Item.Meta
                  title="Name"
                  description={
                    <>
                      {appInfo.app_name || appInfo.base_url || appInfo.client_id}
                      {appInfo.visibility === CustomOAuthAppVisibility.AdminsOnly && (
                        <AdminOnlyTag>Admin only</AdminOnlyTag>
                      )}
                    </>
                  }
                />
                <ManageAction>
                  <ManageText>Manage</ManageText>
                  <CaretRightBlack />
                </ManageAction>
              </StyledItem>
            </RowLink>
          </AppRow>
        ))}

        <Button style={{ marginTop: 16 }} onClick={() => history.push(`${url}?addNewAccount=true`)}>
          Add Connection
        </Button>
      </SettingsLayout>
    );
  }

  return (
    <SettingsLayout
      title={`${displayName} OAuth Settings`}
      dataSourceType={typedDataSourceType}
      width="narrow"
    >
      {/* Only in the read-only view: while an edit is open the settings on screen are
          not the ones that have been configured, which is what the banner asserts. */}
      {isReadOnly && (
        <OAuthConfiguredAlert dataSourceType={typedDataSourceType} $marginBottom="30px" />
      )}

      <StyledForm form={form} layout="vertical" onFinish={handleFinish} disabled={isReadOnly}>
        <FormItem
          label="Name"
          name="app_name"
          rules={[{ required: true, message: "Name is required" }, duplicateAppNameRule]}
        >
          <Input placeholder={copy.appNamePlaceholder} />
        </FormItem>

        <FormItem
          label={`${displayName} API Base URL`}
          name="base_url"
          rules={[{ required: true, message: `${displayName} API Base URL is required` }]}
        >
          <Input placeholder={copy.baseUrlPlaceholder} />
        </FormItem>

        <FormItem
          label="Visibility"
          name="visibility"
          tooltip={{
            // Wider than the 250px default: two labelled sections at that width stack
            // into a very tall, thin column.
            overlayStyle: { maxWidth: 340 },
            title: (
              <TooltipBody>
                <p>
                  <strong>All users</strong>
                  <br />
                  All users in your domain(s) can create a data source connection using this
                  endpoint configuration.
                </p>
                <p>
                  <strong>Admin users only</strong>
                  <br />
                  Only admins can create a data source connection using this endpoint configuration.
                  Use this option to prevent users from accidentally connecting to endpoints such as
                  test or staging environments.
                </p>
              </TooltipBody>
            ),
          }}
        >
          <BorderedSelect
            size="middle"
            style={{ width: "100%" }}
            options={[
              { value: CustomOAuthAppVisibility.AllUsers, label: "All users" },
              { value: CustomOAuthAppVisibility.AdminsOnly, label: "Admin users only" },
            ]}
          />
        </FormItem>

        <Section>
          <SectionHeading>OAuth Configuration</SectionHeading>

          <FormItem
            label="Client ID"
            name="client_id"
            rules={[{ required: true, message: "Client ID is required" }, duplicateClientIdRule]}
          >
            <Input disabled={isExistingApp} placeholder={copy.clientIdPlaceholder} />
          </FormItem>

          <FormItem
            label="Client Secret"
            name="client_secret"
            // Only required when creating. The API never returns a stored secret, so
            // requiring it on every edit would mean an admin cannot fix a URL without
            // tracking down the original credential; blank means "keep the current one".
            rules={isExistingApp ? [] : [{ required: true, message: "Client Secret is required" }]}
            tooltip={
              isExistingApp
                ? "Leave blank to keep the current secret. Enter a new one to replace it."
                : undefined
            }
          >
            <Input
              type="password"
              autoComplete="new-password"
              placeholder={isExistingApp ? "******************************" : "Client Secret"}
            />
          </FormItem>

          <FormItem
            label="Issuer URL"
            name="issuer_url"
            dependencies={["issuer_url", "authorization_url", "token_url"]}
            rules={[endpointsResolvableRule]}
            tooltip={{
              // Wider than the 250px default: this is two paragraphs, and the field names
              // wrap badly at the default width.
              overlayStyle: { maxWidth: 340 },
              title: (
                <TooltipBody>
                  <p>
                    Enter your authorization server's <strong>Issuer URL</strong> and we will look
                    up <strong>Authorization URL</strong> and <strong>Token URL</strong> from its
                    published configuration metadata.
                  </p>
                  <p>
                    You could also leave <strong>Issuer URL</strong> blank, and enter{" "}
                    <strong>Authorization URL</strong> and <strong>Token URL</strong> instead.
                  </p>
                </TooltipBody>
              ),
            }}
          >
            <Input placeholder={copy.issuerUrlPlaceholder} />
          </FormItem>

          <FormItem
            label="Authorization URL"
            name="authorization_url"
            dependencies={["issuer_url", "authorization_url", "token_url"]}
            rules={[pairedWithRule("token_url", "Authorization URL")]}
          >
            <Input placeholder="https://idp.example.com/authorize" />
          </FormItem>

          <FormItem
            label="Token URL"
            name="token_url"
            dependencies={["issuer_url", "authorization_url", "token_url"]}
            rules={[pairedWithRule("authorization_url", "Token URL")]}
          >
            <Input placeholder="https://idp.example.com/token" />
          </FormItem>

          <FormItem
            label="Scopes"
            name="scopes"
            tooltip="Space separated. Include offline_access so scheduled imports can refresh."
          >
            <Input placeholder={copy.scopesPlaceholder} />
          </FormItem>

          {/* Netflix signs in through its own auth server, which is none of the listed
              providers, so there is nothing to pick. */}
          {!isNetflixQuiver && (
            <FormItem label="Provider Type" name="id_provider">
              <BorderedSelect
                allowClear
                size="middle"
                style={{ width: "100%" }}
                placeholder="Not specified"
                options={Object.values(IdentityProvider).map(value => ({
                  value,
                  label: getIdProviderDisplayName(value),
                }))}
              />
            </FormItem>
          )}

          <FormItem
            label="Response Mode"
            name="response_mode_override"
            tooltip="Leave blank unless your provider rejects the connector's default."
          >
            <BorderedSelect
              allowClear
              size="middle"
              style={{ width: "100%" }}
              placeholder={copy.responseModePlaceholder}
              options={Object.values(SSOAuthorizationURLResponseMode).map(value => ({
                value,
                label: value,
              }))}
            />
          </FormItem>
        </Section>

        <FormItem
          label="Runtime Configuration"
          name="extra_config"
          rules={[jsonObjectRule]}
          tooltip={`Passed to ${displayName} as "runtime_configuration" when Coefficient lists the available views.`}
        >
          <Input.TextArea rows={3} placeholder='{"key": "value"}' />
        </FormItem>

        {/* Quiver's view list spans every namespace -- thousands of views -- so an admin
            can narrow the picker to the ones their users need. The connector never
            filters a saved import's view, and users can opt out per import. */}
        {isNetflixQuiver && (
          <FormItem
            label="Default Visible Views"
            name="view_glob_patterns"
            rules={[viewPatternsRule]}
            tooltip={{
              overlayStyle: { maxWidth: 460 },
              title: (
                <TooltipBody>
                  <p>
                    Provide view name patterns to specify which views are visible by default. Users
                    can toggle "Show all namespaces and views" on the view picker to see all views.
                  </p>
                  <ul>
                    <li>One pattern per line</li>
                    <li>
                      <code>*</code> matches within one dotted segment
                    </li>
                    <li>
                      <code>**</code> matches any number of segments
                    </li>
                  </ul>
                  <table>
                    <thead>
                      <tr>
                        <th>Pattern</th>
                        <th>
                          <CheckOutlined /> Matches
                        </th>
                        <th>
                          <CloseOutlined /> Does not match
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {VIEW_PATTERN_EXAMPLES.map(({ pattern, matches, misses }) => (
                        <tr key={pattern}>
                          <td>
                            <code>{pattern}</code>
                          </td>
                          <td>
                            {matches.map(name => (
                              <code key={name}>{name}</code>
                            ))}
                          </td>
                          <td>
                            {misses.map(name => (
                              <code key={name}>{name}</code>
                            ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TooltipBody>
              ),
            }}
          >
            <Input.TextArea rows={4} placeholder={"growth.**\nads.cubes.*"} />
          </FormItem>
        )}

        {/* antd's `Form disabled` cascades through DisabledContext, which Button reads
            too, so this row opts out explicitly -- otherwise Edit would disable itself and
            leave the page with no way out of read-only. */}
        {isReadOnly ? (
          <OAuthActions>
            <OAuthEditButton
              htmlType="button"
              disabled={false}
              onClick={() => setIsEditMode(true)}
            />
            <OAuthDeleteButton
              htmlType="button"
              disabled={false}
              onClick={() => setShowDeleteConfirm(true)}
            />
          </OAuthActions>
        ) : (
          <OAuthActions>
            <OAuthSaveButton htmlType="submit" loading={upsert.isLoading} />
            {isEditMode && (
              <OAuthCancelButton
                htmlType="button"
                onClick={handleCancelEdit}
                disabled={upsert.isLoading}
              />
            )}
          </OAuthActions>
        )}
      </StyledForm>

      <Modal
        open={showDeleteConfirm}
        title={`Delete this ${displayName} OAuth app?`}
        okText="Delete"
        okButtonProps={{ danger: true, loading: remove.isLoading }}
        onOk={() => remove.mutate()}
        onCancel={() => setShowDeleteConfirm(false)}
      >
        {existing?.data_source_count ? (
          <p>
            <strong>
              {existing.data_source_count} connection
              {existing.data_source_count === 1 ? "" : "s"}
            </strong>{" "}
            currently use this app. Deleting it will stop those connections from refreshing.
          </p>
        ) : (
          <p>No connections are currently using this app.</p>
        )}
      </Modal>
    </SettingsLayout>
  );
};
